import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Calendar, FileText, X, ChevronLeft, ChevronRight, Expand } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const Lightbox = ({ images, index, onClose, onMove }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowLeft') onMove(1); if (e.key === 'ArrowRight') onMove(-1); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onMove]);
  return (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="achievement-lightbox" className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
    <button data-testid="achievement-lightbox-close" onClick={onClose} className="absolute top-5 left-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
    {images.length > 1 && (
      <>
        <button data-testid="achievement-lightbox-prev" onClick={(e) => { e.stopPropagation(); onMove(-1); }} className="absolute right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
        <button data-testid="achievement-lightbox-next" onClick={(e) => { e.stopPropagation(); onMove(1); }} className="absolute left-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
      </>
    )}
    <img src={images[index]} alt="" className="max-w-full max-h-[88vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
    <div className="absolute bottom-5 text-white/70 text-sm">{index + 1} / {images.length}</div>
  </motion.div>
  );
};

const AchievementCard = ({ a, featured, single, onOpen }) => {
  const img = a.images?.[0];
  return (
    <motion.div {...reveal} data-testid={`achievement-card-${a.id}`} className={`group relative rounded-3xl overflow-hidden bg-[#0B1220] text-white ${featured ? `sm:col-span-2 ${single ? '' : 'lg:row-span-2'} min-h-[420px] lg:min-h-0` : 'min-h-[260px] lg:min-h-0'}`}>
      {img ? (
        <button onClick={() => onOpen(a.images, 0)} className="absolute inset-0 w-full h-full" aria-label="عرض الصورة">
          <img src={img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50" />
          <img src={img} alt={a.title} className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]" />
        </button>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 flex items-center justify-center">
          <Trophy className="w-28 h-28 text-white/25" strokeWidth={1.2} />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6 pt-16 pointer-events-none">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 bg-amber-400 text-[#0B1220] text-xs font-black px-3 py-1 rounded-full"><Trophy className="w-3.5 h-3.5" /> إنجاز</span>
          {a.date && <span className="inline-flex items-center gap-1 text-xs text-slate-300"><Calendar className="w-3.5 h-3.5" /> {a.date}</span>}
        </div>
        <h3 className={`font-black leading-snug ${featured ? 'text-2xl lg:text-3xl' : 'text-lg'}`}>{a.title}</h3>
        {a.description && <p className={`text-slate-300 mt-2 ${featured ? 'text-sm lg:text-base line-clamp-3' : 'text-sm line-clamp-2'}`}>{a.description}</p>}
      </div>
      <div className="absolute top-4 left-4 flex gap-2">
        {a.images?.length > 1 && <button onClick={() => onOpen(a.images, 0)} data-testid={`achievement-gallery-btn-${a.id}`} className="inline-flex items-center gap-1.5 bg-black/50 backdrop-blur text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-black/70"><Expand className="w-3.5 h-3.5" /> {a.images.length} صور</button>}
        {a.files?.map((f, i) => (
          <a key={i} href={f} target="_blank" rel="noopener noreferrer" data-testid={`achievement-file-${a.id}-${i}`} className="inline-flex items-center gap-1.5 bg-white/90 text-[#0B1220] text-xs font-bold px-3 py-1.5 rounded-full hover:bg-white"><FileText className="w-3.5 h-3.5" /> وثيقة {a.files.length > 1 ? i + 1 : ''}</a>
        ))}
      </div>
    </motion.div>
  );
};

const AchievementsPublicDisplay = ({ sectionId = 'achievements' }) => {
  const [items, setItems] = useState([]);
  const [box, setBox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/achievements`)
      .then((r) => setItems((r.data || []).filter((a) => a.isActive !== false)))
      .catch(() => {});
  }, []);

  if (!items.length) return null;
  const open = (images, index) => setBox({ images, index });
  const move = (d) => setBox((b) => ({ ...b, index: (b.index + d + b.images.length) % b.images.length }));

  return (
    <section id={sectionId} data-testid="achievements-section" className="relative bg-[#FAFAF9] py-16 lg:py-24 overflow-hidden" dir="rtl">
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-amber-200/40 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2"><span className="h-[3px] w-8 rounded-full bg-amber-500" /><span className="text-xs font-bold text-amber-600">خزانة البطولات</span></div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900">إنجازات نادي الأمجاد</h2>
            <p className="text-slate-500 mt-2">فخورون بتاريخنا العريق وإنجازاتنا المتميزة</p>
          </div>
          <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-100 px-5 py-3 shadow-sm">
            <Trophy className="w-7 h-7 text-amber-500" />
            <div><div data-testid="achievements-count" className="text-2xl font-black text-slate-900">{items.length}</div><div className="text-xs text-slate-500">إنجاز</div></div>
          </div>
        </div>
        <div className={`grid gap-5 ${items.length === 1 ? 'lg:auto-rows-[420px]' : items.length === 2 ? 'sm:grid-cols-2 lg:grid-cols-3 lg:auto-rows-[420px]' : 'sm:grid-cols-2 lg:grid-cols-3 lg:auto-rows-[260px]'}`}>
          {items.map((a, i) => <AchievementCard key={a.id || i} a={a} featured={i === 0} single={items.length <= 2} onOpen={open} />)}
        </div>
      </div>
      <AnimatePresence>{box && <Lightbox images={box.images} index={box.index} onClose={() => setBox(null)} onMove={move} />}</AnimatePresence>
    </section>
  );
};

export default AchievementsPublicDisplay;
